/**
 * Utility for generating clarifying questions when user input is unclear
 * Can be used across all topic strategies to handle unclear inputs
 */

/**
 * Check if AI matches result contains meaningful requirements for a given topic
 * @param {Object} aiMatchesResult - Result from runAIMatches
 * @param {string} topic - The topic to check (skills, location, company, etc.)
 * @returns {boolean} Whether meaningful requirements were found
 */
export const hasMeaningfulRequirements = (aiMatchesResult, topic) => {
  if (!aiMatchesResult?.search_gen_response?.icp_requirements) {
    return false;
  }

  const requirements = aiMatchesResult.search_gen_response.icp_requirements;

  // Topic-specific requirement checks
  const topicCheckers = {
    skills: (reqs) => {
      const skillReqs = reqs.filter(req => 
        req.section_name === "Must Have Skills" || 
        req.section_name === "Nice to Have Skills" ||
        (req.section_name && req.section_name.toLowerCase().includes('skill'))
      );
      
      // Check if any skill requirements have meaningful bool_str content
      return skillReqs.some(req => {
        if (!req.bool_str || req.bool_str.trim().length === 0) {
          return false;
        }
        
        // Check if bool_str contains actual technical skills indicators
        const content = req.bool_str.toLowerCase();
        const techIndicators = [
          'python', 'javascript', 'java', 'react', 'angular', 'vue', 
          'node', 'django', 'flask', 'sql', 'mongodb', 'aws', 'azure',
          'docker', 'kubernetes', 'git', 'api', 'rest', 'graphql',
          'html', 'css', 'typescript', 'php', 'ruby', 'go', 'rust',
          'spring', 'express', 'postgresql', 'mysql', 'redis'
        ];
        
        // If it contains specific tech terms, it's meaningful
        const hasTechTerms = techIndicators.some(term => content.includes(term));
        
        // If it's just generic terms like "experience" or "development", it's not meaningful
        const genericTermsOnly = /^[(\s]*(\d+\s*\+?\s*)?(year|experience|development|professional|software|programming)[)\s]*$/i.test(req.bool_str.trim());
        
        return hasTechTerms && !genericTermsOnly;
      });
    },
    location: (reqs) => reqs.some(req => 
      req.section_name && req.section_name.toLowerCase().includes('location')
    ),
    company: (reqs) => reqs.some(req => 
      req.section_name && (
        req.section_name.toLowerCase().includes('company') ||
        req.section_name.toLowerCase().includes('organization')
      )
    ),
    experience: (reqs) => reqs.some(req => 
      req.section_name && req.section_name.toLowerCase().includes('experience')
    ),
    title: (reqs) => reqs.some(req => 
      req.section_name && (
        req.section_name.toLowerCase().includes('title') ||
        req.section_name.toLowerCase().includes('role')
      )
    ),
    company_stage: (reqs) => reqs.some(req => 
      req.section_name && req.section_name.toLowerCase().includes('stage')
    )
  };

  const checker = topicCheckers[topic];
  return checker ? checker(requirements) : requirements.length > 0;
};

/**
 * Generate clarifying questions when user input is unclear
 * @param {string} userInput - The original user input
 * @param {string} topic - The current topic (skills, location, etc.)
 * @returns {string} Clarifying question to ask the user
 */
export const generateClarifyingQuestions = (userInput, topic = 'skills') => {
  // Topic-specific clarifying questions with variety
  const topicQuestions = {
    skills: [
      "I want to make sure I understand your technical requirements correctly. Could you help me by specifying the key skills and competencies you're looking for? For example, programming languages, frameworks, or specific technologies?",
      "To find the best candidates, could you provide more details about the technical skills needed? This could include specific tools, languages, or areas of expertise.",
      "Help me understand the technical requirements better. What specific skills, technologies, or competencies are most important for this role?",
      "Could you be more specific about the technical skills you need? For instance, are there particular programming languages, frameworks, or tools that are essential?"
    ],
    location: [
      "I want to understand your location preferences better. Are you looking for candidates in specific cities, states, regions, or are you open to remote work?",
      "Could you clarify your location requirements? Are there specific geographic areas, time zones, or work arrangements you prefer?",
      "Help me understand where you'd like to find candidates. Are there particular locations, regions, or remote work preferences I should know about?",
      "What are your location or remote work preferences? Are you looking for candidates in specific areas or open to distributed teams?"
    ],
    company: [
      "I'd like to understand your company preferences better. Are you looking for candidates from specific types of companies, industries, or company sizes?",
      "Could you provide more details about company background preferences? This could include industry, company stage, or specific organizations.",
      "Help me understand what company experience would be valuable. Are there particular industries, company types, or organizations you'd prefer?",
      "What kind of company background are you looking for? Are there specific industries, company sizes, or types of organizations that would be ideal?"
    ],
    experience: [
      "I want to understand your experience requirements better. Could you specify the level of experience, specific roles, or years of experience you're looking for?",
      "Help me clarify the experience requirements. Are you looking for specific seniority levels, types of experience, or particular career backgrounds?",
      "Could you provide more details about the experience you're seeking? This could include years of experience, specific roles, or areas of expertise.",
      "What level and type of experience are you looking for? Are there specific roles, seniority levels, or areas of expertise that are important?"
    ],
    title: [
      "I'd like to understand the job titles or roles you're targeting. Could you specify the types of positions or role levels you're looking for?",
      "Help me clarify the role requirements. Are there specific job titles, seniority levels, or types of positions you have in mind?",
      "Could you provide more details about the roles you're seeking? This could include specific titles, levels, or types of positions.",
      "What specific job titles or role types are you looking for? Are there particular seniority levels or position categories you have in mind?"
    ],
    company_stage: [
      "I'd like to understand what stage of companies you're interested in. Are you looking for candidates from startups, established companies, or a specific company maturity level?",
      "Could you clarify what types of companies by stage you prefer? For example, early-stage startups, growth companies, or established enterprises?",
      "Help me understand your company stage preferences. Are there particular phases of company development that would be ideal for your search?"
    ]
  };
  
  // Get questions for the topic, fallback to skills if topic not found
  const questions = topicQuestions[topic] || topicQuestions.skills;
  
  // Randomly select one of the questions to add variety
  const randomQuestion = questions[Math.floor(Math.random() * questions.length)];
  
  return randomQuestion;
};

/**
 * Generate copilot-powered clarifying questions using LLM
 * @param {string} userInput - The original user input
 * @param {string} topic - The current topic
 * @param {Object} decision - Decision object containing API access
 * @returns {Promise<string>} Clarifying question generated by copilot
 */
export const generateCopilotClarifyingQuestion = async (userInput, topic, decision) => {
  console.log(`🤖 DEBUGGING: Starting copilot question generation for topic: ${topic}, userInput: "${userInput}"`);
  
  try {
    // Topic-specific context for the copilot
    const topicContexts = {
      skills: "You are helping discover specific technical skills for a job search. The user mentioned experience or general development work, but you need concrete technical skills like programming languages (Python, JavaScript, Java), frameworks (React, Angular, Django), databases (PostgreSQL, MongoDB), cloud platforms (AWS, Azure), or specific tools and technologies.",
      location: "You are helping discover location preferences for a job search. The user mentioned something about location but you need specific details about cities, states, regions, remote work preferences, or geographic requirements.",
      company: "You are helping discover company preferences for a job search. The user mentioned something about companies but you need specific details about company types, industries, sizes, stages, or specific organizations.",
      experience: "You are helping discover experience requirements for a job search. The user mentioned something about experience but you need specific details about years of experience, seniority levels, specific roles, or types of experience.",
      title: "You are helping discover job title preferences for a job search. The user mentioned something about roles but you need specific details about job titles, position levels, or types of positions.",
      company_stage: "You are helping discover company stage preferences for a job search. The user mentioned something about company stages but you need specific details about startup phases, growth stages, or company maturity levels."
    };

    const context = topicContexts[topic] || topicContexts.skills;
    
    // Topic-specific examples for better prompts
    const topicExamples = {
      skills: 'Example: "I see you\'re looking for experienced developers! What specific technical skills are most important - for example, are you looking for expertise in languages like Python or JavaScript, frameworks like React or Django, or other specific technologies?"',
      location: 'Example: "I understand you have location preferences! Could you be more specific - for example, are you looking for candidates in particular cities like San Francisco or Austin, specific states, or are you open to remote work?"',
      company: 'Example: "I see you have company preferences! What types of companies are you thinking - for example, are you interested in startups, established tech companies like Google or Microsoft, or companies in specific industries?"',
      experience: 'Example: "I understand you\'re looking for experienced candidates! Could you clarify the type of experience - for example, are you looking for 3-5 years in specific roles, senior-level positions, or experience with particular technologies?"',
      title: 'Example: "I see you have role preferences! What specific titles are you targeting - for example, are you looking for Software Engineers, Senior Developers, Tech Leads, or other specific positions?"',
      company_stage: 'Example: "I understand you have company stage preferences! What stages are you thinking - for example, early-stage startups, growth companies, or established enterprises?"'
    };

    const example = topicExamples[topic] || topicExamples.skills;

    // Create a focused prompt for clarifying questions
    const clarifyingPrompt = `${context}

User input: "${userInput}"

The user mentioned something general but didn't provide specific ${topic} details. Ask a helpful clarifying question to guide them toward providing the specific information needed. Be conversational and encouraging. Provide 2-3 concrete examples in your question. Keep it under 2 sentences.

${example}

Your response:`;

    console.log(`🤖 DEBUGGING: About to call copilot-custom API`);
    console.log(`🤖 DEBUGGING: clarifyingPrompt:`, clarifyingPrompt);
    
    // Use the proper copilot-custom API instead of regular chat completion
    const { callCopilotCustom } = await import('../api');
    
    // Extract user ID for joid parameter (joid should be the logged-in user's ID)
    console.log(`🤖 DEBUGGING: decision object keys:`, Object.keys(decision));
    console.log(`🤖 DEBUGGING: decision.userId:`, decision.userId);
    
    // Try multiple ways to get the user ID
    const userId = decision.userId || decision.user_id || decision.currentUserId || 'default_user';
    console.log(`🤖 DEBUGGING: Using userId:`, userId);
    const joid = userId;
    
    // Create system message for copilot-custom
    const systemMsg = context;
    
    // Create base message for copilot-custom  
    const baseMsg = clarifyingPrompt;
    
    console.log(`🤖 DEBUGGING: Calling copilot-custom with joid: ${joid}`);
    
    const copilotResult = await callCopilotCustom(joid, systemMsg, baseMsg, [], {
      enforce_json: false,
      custom_purpose: 'clarifying_questions'
    });
    
    console.log(`🤖 DEBUGGING: Full copilot result:`, copilotResult);
    
    // Extract content from the copilot-custom response structure
    const copilotResponse = copilotResult?.custom_chat_response?.content || 
                           copilotResult?.response || 
                           copilotResult?.content;
    
    console.log(`🤖 DEBUGGING: copilotResponse received:`, copilotResponse);
    
    if (copilotResponse && typeof copilotResponse === 'string' && copilotResponse.trim().length > 0) {
      console.log(`🤖 Generated copilot clarifying question for ${topic}:`, copilotResponse);
      return copilotResponse.trim();
    }
    
    console.log(`⚠️ DEBUGGING: Copilot response was empty or invalid, falling back to predefined questions`);
    // Fallback to predefined questions if copilot fails
    return generateClarifyingQuestions(userInput, topic);
    
  } catch (error) {
    console.error('🚨 DEBUGGING: Error generating copilot clarifying question:', error);
    // Fallback to predefined questions
    return generateClarifyingQuestions(userInput, topic);
  }
};

/**
 * Generic function to handle unclear inputs in any strategy using copilot-powered questions
 * @param {Object} aiMatchesResult - Result from runAIMatches
 * @param {string} topic - The current topic
 * @param {Object} latestUserMessage - The latest user message
 * @param {Function} setMessages - Function to update messages
 * @param {Object} decision - Current decision object
 * @returns {Promise<Object|null>} Decision object with clarifying question, or null if requirements are meaningful
 */
export const handleUnclearInput = async (aiMatchesResult, topic, latestUserMessage, setMessages, decision) => {
  console.log(`🔍 DEBUGGING handleUnclearInput called for topic: ${topic}`);
  console.log(`🔍 DEBUGGING aiMatchesResult:`, aiMatchesResult);
  console.log(`🔍 DEBUGGING latestUserMessage:`, latestUserMessage);

  // Check if the response is meaningful
  if (hasMeaningfulRequirements(aiMatchesResult, topic)) {
    console.log(`✅ DEBUGGING: Requirements are meaningful for ${topic}, continuing normal flow`);
    return null; // Requirements are meaningful, continue with normal flow
  }

  console.log(`🤔 ${topic.toUpperCase()} STRATEGY: No meaningful requirements extracted from user input, generating copilot clarifying question`);
  
  // Remove "Typing..." message and clear any timeouts
  if (setMessages) {
    setMessages(prev => prev.filter(msg => msg.content !== 'Typing...'));
  }
  
  // Generate a copilot-powered clarifying question
  const userInput = latestUserMessage?.content || '';
  const clarifyingQuestion = await generateCopilotClarifyingQuestion(userInput, topic, decision);
  
  console.log(`🎯 RETURNING COPILOT CLARIFYING QUESTION: "${clarifyingQuestion}"`);
  
  return {
    ...decision,
    actionType: 'DIRECT_COPILOT_RESPONSE',
    agentGuidance: clarifyingQuestion,
    clarificationMessage: clarifyingQuestion,
    strategyPhase: 'CONVERSATION',
    // Add a flag to prevent this from being overridden
    copilotResponseGenerated: true,
    timestamp: Date.now()
  };
};